/**
 * Tooltip - Floating text panel anchored to a target element with a small
 * arrow. Use `show(text, target?)` / `hide()` to drive visibility.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipOptions extends BaseUIComponentOptions {
  /** Initial tooltip text. */
  text?: string;
  /** Placement relative to the target element. */
  placement?: TooltipPlacement;
  /** Gap in pixels between target and tooltip. */
  offset?: number;
  /** Initial anchor element (optional). */
  target?: HTMLElement | null;
}

export class Tooltip extends BaseUIComponent<TooltipOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Tooltip',
    category: 'feedback',
    summary: 'Floating text panel anchored to a target element with a directional arrow.',
    useCases: ['hover hint', 'help text', 'shortcut hint', 'inline explainer'],
    optionsType: 'TooltipOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/tooltip.html',
      styles: '@engine/assets/ui/styles/tooltip.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private textElement: HTMLElement | null = null;
  private target: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: Tooltip.metadata.assetPaths.template,
      stylesPath: Tooltip.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<TooltipOptions> {
    return {
      position: 'center',
      visible: false,
      customClasses: [],
      customStyles: {},
      text: '',
      placement: 'top',
      offset: 8,
      target: null,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      text: this.options.text,
      placement: this.options.placement,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-tooltip') as HTMLElement | null;
    this.textElement = this.layout.querySelector('[data-tooltip-text]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.options.target) {
      this.target = this.options.target;
    }
  }

  /** Show the tooltip, optionally updating the text and target. */
  public override show(text?: string, target?: HTMLElement | null): void {
    if (typeof text === 'string') this.setText(text);
    if (target !== undefined) this.setTarget(target);
    super.show();
    this.reposition();
  }

  /** Hide the tooltip. */
  public override hide(): void {
    super.hide();
  }

  /** Update only the tooltip text. */
  public setText(text: string): void {
    this.options.text = text;
    if (this.textElement) this.textElement.textContent = text;
  }

  /** Change the placement relative to the target. */
  public setPlacement(placement: TooltipPlacement): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(`placement-${this.options.placement}`);
    this.options.placement = placement;
    this.rootElement.classList.add(`placement-${placement}`);
    this.rootElement.dataset.placement = placement;
    this.reposition();
  }

  /** Set or clear the anchor element. */
  public setTarget(target: HTMLElement | null): void {
    this.target = target;
    this.reposition();
  }

  private reposition(): void {
    if (!this.rootElement || !this.target || !this.isVisible) return;

    const targetRect = this.target.getBoundingClientRect();
    const tipRect = this.rootElement.getBoundingClientRect();
    const offset = this.options.offset;

    let top = 0;
    let left = 0;

    switch (this.options.placement) {
      case 'top':
        top = targetRect.top - tipRect.height - offset;
        left = targetRect.left + targetRect.width / 2 - tipRect.width / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + offset;
        left = targetRect.left + targetRect.width / 2 - tipRect.width / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - tipRect.height / 2;
        left = targetRect.left - tipRect.width - offset;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - tipRect.height / 2;
        left = targetRect.right + offset;
        break;
    }

    this.rootElement.style.top = `${Math.round(top)}px`;
    this.rootElement.style.left = `${Math.round(left)}px`;
  }

  protected override onShow(): void {
    this.reposition();
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.textElement = null;
    this.target = null;
  }
}
